import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Loader2, TrendingUp, Activity, Calendar, Package, Euro, Users } from "lucide-react";
import { useSettings } from "../../lib/useSettings";
import { Link } from "react-router-dom";

function money(cents, currency = "EUR") {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format((cents || 0) / 100);
}
function formatDateFr(iso) {
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

export default function RentalsDashboard() {
  const { settings } = useSettings();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/rentals/dashboard?days=${days}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [days]);

  const currency = settings?.currency || "EUR";

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[color:var(--brand-sky)]"/></div>;
  if (!data) return null;

  const k = data.kpis || {};
  const occColor = k.occupancy_rate >= 75 ? "text-emerald-600" : k.occupancy_rate >= 40 ? "text-sky-600" : "text-amber-600";

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="rentals-dashboard">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Tableau de bord location</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Taux de remplissage, revenus et réservations à venir.</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-40" data-testid="dashboard-period"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 jours</SelectItem>
            <SelectItem value="30">30 jours</SelectItem>
            <SelectItem value="90">90 jours</SelectItem>
            <SelectItem value="180">180 jours</SelectItem>
          </SelectContent>
        </Select>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-5" data-testid="kpi-occupancy">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Taux de remplissage</div>
            <TrendingUp className="w-4 h-4 text-[color:var(--brand-sky)]"/>
          </div>
          <div className={`font-display text-3xl font-black mt-2 ${occColor}`}>{k.occupancy_rate || 0}%</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">sur {days} jours</div>
        </Card>
        <Card className="p-5" data-testid="kpi-revenue">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Chiffre d'affaires</div>
            <Euro className="w-4 h-4 text-emerald-600"/>
          </div>
          <div className="font-display text-3xl font-black mt-2 text-[color:var(--brand-navy)]">{money(k.revenue_cents, currency)}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{k.bookings_count || 0} réservations</div>
        </Card>
        <Card className="p-5" data-testid="kpi-active">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Locations en cours</div>
            <Activity className="w-4 h-4 text-sky-500"/>
          </div>
          <div className="font-display text-3xl font-black mt-2 text-[color:var(--brand-navy)]">{k.active_now || 0}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">en ce moment</div>
        </Card>
        <Card className="p-5" data-testid="kpi-units">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Unités / Produits</div>
            <Package className="w-4 h-4 text-orange-500"/>
          </div>
          <div className="font-display text-3xl font-black mt-2 text-[color:var(--brand-navy)]">{k.total_units || 0}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{k.total_products || 0} produits · {k.upcoming || 0} à venir</div>
        </Card>
      </div>

      {/* Top produits */}
      <Card className="p-5" data-testid="top-products">
        <h2 className="font-display text-xl font-bold text-[color:var(--brand-navy)] mb-4">Top produits (CA)</h2>
        {(data.top_products || []).length === 0 ? (
          <div className="py-8 text-center text-sm text-[hsl(var(--muted-foreground))]">Aucune donnée sur la période</div>
        ) : (
          <div className="space-y-3">
            {data.top_products.map((p, idx) => (
              <div key={p.id} className="flex items-center gap-4 p-3 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/30">
                <div className="w-10 h-10 rounded-full bg-[color:var(--brand-navy)] text-white flex items-center justify-center font-bold text-sm">{idx + 1}</div>
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-[hsl(var(--muted))] shrink-0">
                  {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover"/> : <Package className="w-5 h-5 m-4 opacity-30"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[color:var(--brand-navy)] truncate">{p.name}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{p.units_count} unité{p.units_count > 1 ? "s" : ""} · {p.bookings} réservation{p.bookings > 1 ? "s" : ""}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-[color:var(--brand-navy)]">{money(p.revenue_cents, currency)}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">Taux: <span className="font-semibold">{p.occupancy_rate}%</span></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Upcoming & Active */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5" data-testid="upcoming-rentals">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Prochaines réservations</h2>
            <Link to="/admin/rentals" className="text-xs text-[color:var(--brand-sky)] hover:underline">Voir tout</Link>
          </div>
          {(data.upcoming_rentals || []).length === 0 ? (
            <div className="py-6 text-center text-xs text-[hsl(var(--muted-foreground))]">Rien de prévu</div>
          ) : (
            <div className="space-y-2">
              {data.upcoming_rentals.map(r => (
                <div key={r.id} className="text-sm p-2 rounded border border-[hsl(var(--border))]">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold truncate">{r.product_name}</div>
                    <Badge variant="outline" className="text-xs">{r.unit_name}</Badge>
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2 mt-1">
                    <Calendar className="w-3 h-3"/>{formatDateFr(r.start_at)}
                    <Users className="w-3 h-3 ml-2"/>{r.customer_name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5" data-testid="active-rentals">
          <h2 className="font-display text-lg font-bold text-[color:var(--brand-navy)] mb-3">Locations actives</h2>
          {(data.active_rentals || []).length === 0 ? (
            <div className="py-6 text-center text-xs text-[hsl(var(--muted-foreground))]">Aucune location en cours</div>
          ) : (
            <div className="space-y-2">
              {data.active_rentals.map(r => (
                <div key={r.id} className="text-sm p-2 rounded border border-sky-200 bg-sky-50">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold truncate">{r.product_name}</div>
                    <Badge variant="outline" className="text-xs bg-white">{r.unit_name}</Badge>
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2 mt-1">
                    <Calendar className="w-3 h-3"/>Retour: {formatDateFr(r.end_at)}
                    <Users className="w-3 h-3 ml-2"/>{r.customer_name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
